import React, { useState } from "react";
import { Grid, Header, Icon, Image, Label, List, Message, Popup, Segment, Table } from "semantic-ui-react";
import {
  formatSoldDate,
  formatDollar,
  formatPricePerSqft,
  computePercentDifference,
  isConditionUnknown,
  ImpactIcon,
  COMP_STRENGTH_COLOR,
  COMP_STRENGTH_EXPLANATION,
  ANALYTICAL_ROLE_LABEL,
  SOURCE_TIER_LABEL,
  VALUATION_ROLE_LABEL,
  VALUATION_ROLE_COLOR,
  VALUATION_WEIGHT_LABEL,
  buildSiteSearchUrl,
  CONFIDENCE_COLOR,
  HOME_STATUS_LABEL,
} from "./pricingStrategyFormatting";

// One comp card - pure function of `comp`, so it's shared unmodified between
// the live generation view and the read-only "View" for a previous property.
function CompCard({ comp, idx }) {
  const pricePerSqft = formatPricePerSqft(comp.salePrice, comp.squareFeet);
  const siteUrl = comp.source === "Web Search" ? buildSiteSearchUrl(comp.sourceSite, comp.address) : null;
  return (
    <Segment key={idx} style={{ padding: "12px 14px" }}>
      {comp.imageUrl && (
        <Image
          src={comp.imageUrl}
          style={{ width: "100%", height: "120px", objectFit: "cover", marginBottom: "8px", borderRadius: "4px" }}
        />
      )}
      <strong>{comp.address || "Address not provided"}</strong>
      {comp.sameBuilding && <Label size="tiny" style={{ marginLeft: "6px" }}>Same Building</Label>}
      {/* Only shown when analyticalRole is present (new-generation data) -
          the grouping itself already conveys this for older saved
          generations, which this leaves unchanged. */}
      {comp.analyticalRole && comp.compStrength && (
        <Label size="tiny" color={COMP_STRENGTH_COLOR[comp.compStrength]} style={{ marginLeft: "6px" }}>
          {comp.compStrength}
        </Label>
      )}
      {comp.analyticalRole === "expanded" && (
        <Label size="tiny" basic style={{ marginLeft: "6px" }}>
          {ANALYTICAL_ROLE_LABEL.expanded}
        </Label>
      )}
      {comp.sourceTier === "unverified" && (
        <Label size="tiny" basic color="orange" style={{ marginLeft: "6px" }}>
          {SOURCE_TIER_LABEL.unverified}
        </Label>
      )}
      <div className="text-grey" style={{ fontSize: "0.88em", marginTop: "2px" }}>
        {[
          comp.salePrice,
          comp.saleDate && formatSoldDate(comp.saleDate),
          comp.squareFeet && `${comp.squareFeet} sqft`,
          comp.bedsBaths,
          pricePerSqft,
        ].filter(Boolean).join(" · ")}
      </div>
      {(comp.valuationRole || comp.valuationWeight) && (
        <div style={{ marginTop: "6px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {comp.valuationRole && VALUATION_ROLE_LABEL[comp.valuationRole] && (
            <Label size="tiny" color={VALUATION_ROLE_COLOR[comp.valuationRole]}>
              {VALUATION_ROLE_LABEL[comp.valuationRole]}
            </Label>
          )}
          {comp.valuationWeight && comp.valuationWeight !== "none" && VALUATION_WEIGHT_LABEL[comp.valuationWeight] && (
            <Label size="tiny" basic>
              {VALUATION_WEIGHT_LABEL[comp.valuationWeight]}
            </Label>
          )}
        </div>
      )}
      {comp.relevance && (
        <p style={{ marginTop: "6px", marginBottom: 0, fontSize: "0.92em" }}>{comp.relevance}</p>
      )}
      {comp.keyDifferences?.length > 0 && (
        <List size="small" style={{ marginTop: "6px" }}>
          {comp.keyDifferences.map((diff, i) => (
            <List.Item key={i}>
              <ImpactIcon impact={diff.direction} />{" "}
              <strong>{diff.label}:</strong> {diff.note}
            </List.Item>
          ))}
        </List>
      )}
      {comp.similarity && (
        <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {["propertyType", "size", "layout", "bathroomLayout", "condition", "age", "location"]
            .filter((cat) => comp.similarity[cat])
            .map((cat) => (
              <Label key={cat} basic size="tiny">
                {cat === "bathroomLayout" ? "Bath Layout" : cat === "propertyType" ? "Property Type" : cat.charAt(0).toUpperCase() + cat.slice(1)}: {comp.similarity[cat]}
              </Label>
            ))}
        </div>
      )}
      <div className="text-grey" style={{ fontSize: "0.78em", marginTop: "8px" }}>
        {siteUrl ? (
          <>
            Source:{" "}
            <a href={siteUrl} target="_blank" rel="noopener noreferrer">
              Web Search ({comp.sourceSite})
            </a>
          </>
        ) : (
          `Source: ${comp.source || "Unknown"}`
        )}
      </div>
    </Segment>
  );
}

// Comparable Sales - Strong/Moderate shown prominently; Context Only moved
// to a separate, collapsed "Broader Market Context" section. Identical for
// Buyer and Seller (section 17-20 of the Buyer redesign already found this
// structure sound - primary evidence up front, weaker/contextual comps a
// click away) - shared so it can never drift between the two.
function ComparableSalesSection({ result, compSearchSummary, showBroaderMarketContext, setShowBroaderMarketContext }) {
  if (!(result?.comparableSales?.length > 0)) return null;

  // New-generation data carries a deterministic analyticalRole per comp
  // (functions/index.js's compClassification.classifyComparable) - group by
  // Direct/Expanded Comparable Sales instead of just Strong/Moderate.
  // Strong/Moderate still shows as a per-card badge (see CompCard). Context-
  // only entries are rare now (the comp-selection step never auto-pads with
  // them - they only appear via an Agent-Provided mention) and get a small,
  // de-emphasized trailing note instead of a padded grid of their own.
  //
  // Older saved generations predate analyticalRole entirely - this falls
  // back to the original Strong/Moderate-grid + collapsed Context-Only
  // rendering, completely unchanged, whenever it's absent.
  const hasAnalyticalRole = result.comparableSales.some((c) => c.analyticalRole);
  const directComps = hasAnalyticalRole ? result.comparableSales.filter((c) => c.analyticalRole === "direct") : [];
  const expandedComps = hasAnalyticalRole ? result.comparableSales.filter((c) => c.analyticalRole === "expanded") : [];

  const primaryComps = result.comparableSales.filter((c) => (c.compStrength || "Context Only") !== "Context Only");
  const legacyContextComps = result.comparableSales.filter((c) => (c.compStrength || "Context Only") === "Context Only");
  const roleContextComps = result.comparableSales.filter((c) => !c.analyticalRole || c.analyticalRole === "context_only");
  const contextComps = hasAnalyticalRole ? roleContextComps : legacyContextComps;

  return (
    <>
      <Header as="h5" color="blue" style={{ marginTop: "16px", marginBottom: "8px" }}>
        Comparable Sales
        <Popup
          trigger={<Icon name="info circle" color="grey" style={{ marginLeft: "6px", cursor: "pointer" }} />}
          position="top center"
          size="small"
          content={
            <List bulleted>
              <List.Item><strong>Strong:</strong> {COMP_STRENGTH_EXPLANATION.Strong}</List.Item>
              <List.Item><strong>Moderate:</strong> {COMP_STRENGTH_EXPLANATION.Moderate}</List.Item>
              <List.Item><strong>Context Only:</strong> {COMP_STRENGTH_EXPLANATION["Context Only"]}</List.Item>
            </List>
          }
        />
      </Header>
      {compSearchSummary && (
        <p className="text-grey" style={{ fontSize: "0.85em", marginTop: 0 }}>
          {compSearchSummary.candidatesUsable > 0
            ? `Public record search expanded to ${compSearchSummary.passesRun} pass${compSearchSummary.passesRun === 1 ? "" : "es"}${compSearchSummary.finalRadiusMiles ? ` (up to ~${compSearchSummary.finalRadiusMiles} mi)` : ""}, last ${compSearchSummary.finalMonths} months — ${compSearchSummary.candidatesUsable} usable candidate(s) found.`
            : "No additional public-record comps were found nearby, even after an expanded search — the comps below are from web search and/or agent notes."}
        </p>
      )}
      {hasAnalyticalRole ? (
        directComps.length === 0 && expandedComps.length === 0 ? (
          <p className="text-grey" style={{ fontSize: "0.85em" }}>
            No Strong or Moderate comps were found - the recommendation below relies on broader market
            context only, and confidence is calibrated down accordingly.
          </p>
        ) : (
          <>
            {directComps.length > 0 && (
              <>
                <Header as="h5" color="blue" style={{ marginBottom: "8px" }}>
                  {ANALYTICAL_ROLE_LABEL.direct} ({directComps.length})
                </Header>
                <Grid stackable columns={2} style={{ marginBottom: expandedComps.length > 0 ? "16px" : 0 }}>
                  {directComps.map((comp, idx) => <Grid.Column key={idx}><CompCard comp={comp} idx={idx} /></Grid.Column>)}
                </Grid>
              </>
            )}
            {expandedComps.length > 0 && (
              <>
                <Header as="h5" style={{ marginBottom: "4px" }}>
                  {ANALYTICAL_ROLE_LABEL.expanded} ({expandedComps.length})
                </Header>
                <p className="text-grey" style={{ fontSize: "0.85em", marginTop: 0, marginBottom: "8px" }}>
                  From a broader area or a competing neighborhood - still real evidence, see each card's notes.
                </p>
                <Grid stackable columns={2}>
                  {expandedComps.map((comp, idx) => <Grid.Column key={idx}><CompCard comp={comp} idx={idx} /></Grid.Column>)}
                </Grid>
              </>
            )}
          </>
        )
      ) : primaryComps.length > 0 ? (
        <Grid stackable columns={2}>
          {["Strong", "Moderate"].map((tier) => {
            const tierComps = primaryComps.filter((c) => (c.compStrength || "Context Only") === tier);
            if (tierComps.length === 0) return null;
            return (
              <Grid.Column key={tier}>
                <Header as="h5" color={COMP_STRENGTH_COLOR[tier]} style={{ marginBottom: "8px" }}>
                  {tier} ({tierComps.length})
                </Header>
                {tierComps.map((comp, idx) => <CompCard key={idx} comp={comp} idx={idx} />)}
              </Grid.Column>
            );
          })}
        </Grid>
      ) : (
        <p className="text-grey" style={{ fontSize: "0.85em" }}>
          No Strong or Moderate comps were found - the recommendation below relies on broader market
          context only, and confidence is calibrated down accordingly.
        </p>
      )}

      {contextComps.length > 0 && (
        <Segment secondary style={{ marginTop: "16px" }}>
          <div
            style={{ cursor: "pointer", display: "flex", justifyContent: "space-between" }}
            onClick={() => setShowBroaderMarketContext((v) => !v)}
          >
            <div>
              <Header as="h5" style={{ marginBottom: "4px" }}>
                {hasAnalyticalRole ? "Additional Context" : "Broader Market Context"} ({contextComps.length})
              </Header>
              <p className="text-grey" style={{ margin: 0, fontSize: "0.85em" }}>
                {COMP_STRENGTH_EXPLANATION["Context Only"]}
              </p>
            </div>
            <Icon name={showBroaderMarketContext ? "chevron up" : "chevron down"} />
          </div>
          {showBroaderMarketContext && (
            <Grid stackable columns={2} style={{ marginTop: "10px" }}>
              {contextComps.map((comp, idx) => (
                <Grid.Column key={idx}><CompCard comp={comp} idx={idx} /></Grid.Column>
              ))}
            </Grid>
          )}
        </Segment>
      )}
    </>
  );
}

// "How We Reached the Value Range" - reconciles the recommended price to
// specific comps. Identical for Buyer and Seller.
function ValuationReconciliationSection({ result }) {
  if (!(result?.valuationReconciliation?.lowerSupport || result?.valuationReconciliation?.coreSupport || result?.valuationReconciliation?.upperSupport)) {
    return null;
  }
  return (
    <>
      <Header as="h5" color="blue" style={{ marginTop: "16px", marginBottom: "8px" }}>
        How We Reached the Value Range
      </Header>
      <Grid stackable columns={3}>
        {[
          { key: "lowerSupport", label: "Lower Range", color: "blue" },
          { key: "coreSupport", label: "Core Price", color: "green" },
          { key: "upperSupport", label: "Upper Range / Context", color: "purple" },
        ].map(({ key, label, color }) => {
          const band = result.valuationReconciliation[key];
          if (!band) return null;
          return (
            <Grid.Column key={key}>
              <Segment style={{ padding: "10px 14px" }}>
                <Label color={color} size="tiny" style={{ marginBottom: "6px" }}>{label}</Label>
                <div style={{ fontWeight: 700, fontSize: "1.05em" }}>
                  {band.low} &ndash; {band.high}
                </div>
                {band.reason && (
                  <p style={{ fontSize: "0.88em", marginTop: "6px", marginBottom: 0 }}>{band.reason}</p>
                )}
              </Segment>
            </Grid.Column>
          );
        })}
      </Grid>
    </>
  );
}

// One competing-listing card - active/current asking price, NEVER a closed
// sale (see ComparableSalesSection above for actual valuation evidence).
// Pure function of `listing`, shared between Buyer and Seller like CompCard.
function CompetingListingCard({ listing }) {
  const details = [
    Number.isFinite(listing.listPrice) ? formatDollar(listing.listPrice) : null,
    Number.isFinite(listing.originalListPrice) && listing.originalListPrice !== listing.listPrice
      ? `was ${formatDollar(listing.originalListPrice)}`
      : null,
    Number.isFinite(listing.daysOnMarket) ? `~${listing.daysOnMarket} days on market` : null,
    listing.squareFeet && `${listing.squareFeet} sqft`,
    listing.bedrooms && listing.bathrooms ? `${listing.bedrooms} bed / ${listing.bathrooms} bath` : null,
    listing.propertyType,
    listing.yearBuilt && `built ${listing.yearBuilt}`,
    Number.isFinite(listing.distanceMiles) && `${listing.distanceMiles} mi away`,
  ].filter(Boolean);
  return (
    <Segment style={{ padding: "12px 14px" }}>
      <strong>{listing.address || "Address not provided"}</strong>
      <div className="text-grey" style={{ fontSize: "0.88em", marginTop: "2px" }}>
        {details.join(" · ")}
      </div>
      {listing.competitiveRelationship && (
        <p style={{ marginTop: "6px", marginBottom: 0, fontSize: "0.92em" }}>{listing.competitiveRelationship}</p>
      )}
    </Segment>
  );
}

// "What Buyers Can Buy Instead" (Seller) / "Competing Listings" (Buyer) -
// currently available alternatives at a similar price point, deterministically
// selected and price-band-filtered server side (functions/index.js's
// valuationConsistency.buildCompetingListings) - NEVER comparable-sales
// evidence (see ComparableSalesSection), informs competitive positioning
// only. Shared between Buyer/Seller so the two can never drift apart.
// Older saved generations predate `competingListings` entirely and fall
// back to the original plain-paragraph rendering, unchanged.
function CompetingListingsSection({ result, title }) {
  const listings = result?.competingListings;
  if (!Array.isArray(listings)) {
    if (!result?.activeCompetitionAnalysis) return null;
    return (
      <Message info style={{ marginTop: "16px" }}>
        <Message.Header>Active Competition</Message.Header>
        <p style={{ marginTop: "6px" }}>{result.activeCompetitionAnalysis}</p>
      </Message>
    );
  }
  return (
    <>
      <Header as="h5" color="blue" style={{ marginTop: "16px", marginBottom: "8px" }}>
        {title}
      </Header>
      {result?.activeCompetitionAnalysis && (
        <p className="text-grey" style={{ marginTop: 0, marginBottom: "10px", fontSize: "0.9em" }}>
          {result.activeCompetitionAnalysis}
        </p>
      )}
      {listings.length > 0 ? (
        <Grid stackable columns={2}>
          {listings.map((listing, idx) => (
            <Grid.Column key={idx}><CompetingListingCard listing={listing} /></Grid.Column>
          ))}
        </Grid>
      ) : (
        <p className="text-grey" style={{ fontSize: "0.85em" }}>
          No sufficiently comparable active alternatives were identified in the current search.
        </p>
      )}
    </>
  );
}

// Condition Impact - full detail table, collapsed by default. Identical for
// Buyer and Seller (the Buyer redesign's early compact warning, when
// condition is unknown, is separate and rendered near the top instead -
// this full section still exists further down either way, so nothing about
// condition impact is ever hidden, only surfaced earlier for Buyer).
function ConditionImpactSection({ result, conditionHasItems, showConditionDetail, setShowConditionDetail }) {
  if (!result?.conditionImpact) return null;
  return (
    <Segment secondary style={{ marginTop: "16px" }}>
      <div
        style={{ cursor: conditionHasItems ? "pointer" : "default", display: "flex", justifyContent: "space-between" }}
        onClick={() => conditionHasItems && setShowConditionDetail((v) => !v)}
      >
        <div>
          <Header as="h5" style={{ marginBottom: "4px" }}>Condition Impact</Header>
          <p style={{ margin: 0 }}>{result.conditionImpact.summary}</p>
          {!conditionHasItems && (
            <p className="text-grey" style={{ fontSize: "0.85em", marginTop: "4px", marginBottom: 0 }}>
              Click "Edit Details &amp; Regenerate" button below to add the home's condition, updates,
              or known issues and include them in the pricing strategy.
            </p>
          )}
        </div>
        {conditionHasItems && <Icon className="no-print" name={showConditionDetail ? "chevron up" : "chevron down"} />}
      </div>
      {conditionHasItems && (
        <div
          className="strategy-collapsible"
          style={{ display: showConditionDetail ? "block" : "none", overflowX: "auto", marginTop: "10px" }}
        >
          <Table celled compact size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Item</Table.HeaderCell>
                <Table.HeaderCell>Buyer-Perceived Impact</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {result.conditionImpact.items.map((row, idx) => (
                <Table.Row key={idx}>
                  <Table.Cell>{row.item}</Table.Cell>
                  <Table.Cell>{row.low} &ndash; {row.high}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </Segment>
  );
}

// The core, purely `result`-driven body of a Pricing Strategy. Shared
// between StrategyPricingModal.jsx (the live/current-property view) and
// StrategyResultViewerModal.jsx (the read-only "View" for a previous
// property) - previously each maintained its own copy of this rendering,
// and the read-only one had drifted into a much plainer, unformatted
// version of the same data.
//
// Seller (List Pricing Strategy) output below is UNCHANGED from before the
// Buyer Offer Pricing redesign - same sections, same order, same field
// names - per the explicit instruction to restructure only the Buyer side.
// Buyer (Offer Pricing Strategy) output is a separate, purpose-built
// "offer decision tool" layout: what to offer, why, how far to move, what
// the Seller/listing context suggests, what evidence supports it, and what
// to do next - see BuyerOfferDecisionBody below.
export default function PricingStrategyResultBody({ result, isBuyer, compSearchSummary, canRegenerate }) {
  const [showBroaderMarketContext, setShowBroaderMarketContext] = useState(false);
  const [showConditionDetail, setShowConditionDetail] = useState(false);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [showMarketCrossChecks, setShowMarketCrossChecks] = useState(false);

  const conditionHasItems = result?.conditionImpact?.items?.length > 0;

  if (isBuyer) {
    return (
      <BuyerOfferDecisionBody
        result={result}
        compSearchSummary={compSearchSummary}
        canRegenerate={canRegenerate}
        conditionHasItems={conditionHasItems}
        showBroaderMarketContext={showBroaderMarketContext}
        setShowBroaderMarketContext={setShowBroaderMarketContext}
        showConditionDetail={showConditionDetail}
        setShowConditionDetail={setShowConditionDetail}
        showMarketCrossChecks={showMarketCrossChecks}
        setShowMarketCrossChecks={setShowMarketCrossChecks}
      />
    );
  }

  const hasNegotiationRange = result?.expectedNegotiationLow && result?.expectedNegotiationHigh;

  return (
    <>
      {/* Compact summary - the most important information, visible without scrolling */}
      <Grid stackable columns={hasNegotiationRange ? 4 : 3} style={{ marginBottom: "4px" }}>
        <Grid.Column>
          <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
            RECOMMENDED LIST PRICE
          </div>
          <div style={{ fontSize: "1.6em", fontWeight: 700 }}>{result?.recommendedListPrice}</div>
        </Grid.Column>
        <Grid.Column>
          <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
            ESTIMATED MARKET VALUE
          </div>
          <div style={{ fontSize: "1.15em", fontWeight: 600 }}>
            {result?.estimatedValueLow} &ndash; {result?.estimatedValueHigh}
          </div>
        </Grid.Column>
        <Grid.Column>
          <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
            CONFIDENCE
          </div>
          <Label color={CONFIDENCE_COLOR[result?.confidence] || "grey"} style={{ marginTop: "2px" }}>
            {result?.confidence || "Unknown"}
          </Label>
        </Grid.Column>
        {hasNegotiationRange && (
          <Grid.Column>
            <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
              EXPECTED NEGOTIATION RANGE
            </div>
            <div style={{ fontSize: "1.15em", fontWeight: 600 }}>
              {result.expectedNegotiationLow} &ndash; {result.expectedNegotiationHigh}
            </div>
          </Grid.Column>
        )}
      </Grid>

      {result?.confidenceExplanation && (
        <p className="text-grey" style={{ fontSize: "0.88em", marginTop: 0 }}>
          {result.confidenceExplanation}
          {canRegenerate && (result?.confidence === "Low" || result?.confidence === "Low-Moderate") && (
            " Click “Edit Details & Regenerate” to fill in more information and re-run this Strategy for a better estimate."
          )}
        </p>
      )}

      {result?.strategySentence && (
        <p style={{ fontWeight: 500, marginBottom: "4px" }}>{result.strategySentence}</p>
      )}
      {result?.summary && <p className="text-grey" style={{ marginTop: 0 }}>{result.summary}</p>}

      {/* Why this price */}
      {result?.keyFactors?.length > 0 && (
        <>
          <Header as="h5" color="blue" style={{ marginBottom: "8px" }}>Why This Price?</Header>
          <Grid stackable columns={2}>
            {result.keyFactors.map((factor, idx) => (
              <Grid.Column key={idx}>
                <Segment style={{ padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                    <ImpactIcon impact={factor.impact} />
                    <strong style={{ fontSize: "0.85em", letterSpacing: "0.02em" }}>
                      {factor.title?.toUpperCase()}
                    </strong>
                  </div>
                  <p style={{ margin: "4px 0 0" }}>{factor.summary}</p>
                  {factor.supportingValue && (
                    <div className="text-grey" style={{ fontSize: "0.85em", marginTop: "4px" }}>
                      {factor.supportingValue}
                    </div>
                  )}
                </Segment>
              </Grid.Column>
            ))}
          </Grid>
        </>
      )}

      {/* Pricing approaches */}
      {result?.pricingApproaches?.length > 0 && (
        <>
          <Header as="h5" color="blue" style={{ marginTop: "16px", marginBottom: "8px" }}>
            Pricing Approaches
          </Header>
          <Grid stackable columns={3}>
            {result.pricingApproaches.map((approach, idx) => (
              <Grid.Column key={idx}>
                <Segment style={approach.recommended ? { borderTop: "3px solid #2185d0" } : undefined}>
                  {approach.recommended && (
                    <Label color="blue" ribbon style={{ marginBottom: "8px" }}>Recommended</Label>
                  )}
                  <Header as="h5" style={{ marginBottom: "2px" }}>{approach.name}</Header>
                  <div style={{ fontSize: "1.1em", fontWeight: 700, marginBottom: "6px" }}>
                    {approach.price}
                  </div>
                  <p style={{ fontSize: "0.92em" }}>{approach.explanation}</p>
                  {approach.advantages?.length > 0 && (
                    <List bulleted size="small">
                      {approach.advantages.map((item, i) => (
                        <List.Item key={i}>{item}</List.Item>
                      ))}
                    </List>
                  )}
                  {approach.tradeoffs?.length > 0 && (
                    <>
                      <div className="text-grey" style={{ fontWeight: 600, fontSize: "0.82em", marginTop: "8px" }}>
                        TRADEOFFS
                      </div>
                      <List bulleted size="small">
                        {approach.tradeoffs.map((item, i) => (
                          <List.Item key={i}>{item}</List.Item>
                        ))}
                      </List>
                    </>
                  )}
                </Segment>
              </Grid.Column>
            ))}
          </Grid>
        </>
      )}

      <ComparableSalesSection
        result={result}
        compSearchSummary={compSearchSummary}
        showBroaderMarketContext={showBroaderMarketContext}
        setShowBroaderMarketContext={setShowBroaderMarketContext}
      />

      <ValuationReconciliationSection result={result} />

      {/* Valuation cross-checks - FHFA HPI market-trend reference is supporting context only, never
          the estimate itself. Only rendered when we actually have a verified-prior-sale-derived
          figure to show - never a forced/placeholder row. */}
      {result?.historicalMarketContext?.fhfa?.available &&
        Number.isFinite(result.historicalMarketContext.comparison?.broadMarketIndexedReference) && (
          <>
            <Header as="h5" color="blue" style={{ marginTop: "16px", marginBottom: "8px" }}>
              Valuation Cross-Checks
            </Header>
            <Grid stackable columns={2}>
              <Grid.Column>
                <Segment style={{ padding: "10px 14px", height: "100%" }}>
                  <Label size="tiny" color="green" style={{ marginBottom: "6px" }}>Comparable Sales</Label>
                  <div style={{ fontWeight: 700, fontSize: "1.05em" }}>
                    {result?.estimatedValueLow} &ndash; {result?.estimatedValueHigh}
                  </div>
                  <p style={{ fontSize: "0.88em", marginTop: "6px", marginBottom: 0 }}>
                    Primary evidence - based on current comparable sales above.
                  </p>
                </Segment>
              </Grid.Column>
              <Grid.Column>
                <Segment style={{ padding: "10px 14px", height: "100%" }}>
                  <Label size="tiny" basic style={{ marginBottom: "6px" }}>FHFA Market Trend Reference</Label>
                  <div style={{ fontWeight: 700, fontSize: "1.05em" }}>
                    ${Number(result.historicalMarketContext.comparison.broadMarketIndexedReference).toLocaleString()}
                  </div>
                  <p style={{ fontSize: "0.88em", marginTop: "6px", marginBottom: "4px" }}>
                    Based on the subject's prior sale ({result.historicalMarketContext.fhfa.priorPeriod?.period}) and
                    the {result.historicalMarketContext.fhfa.geography?.geoName} FHFA Expanded-Data HPI (
                    {result.historicalMarketContext.fhfa.currentPeriod?.period}
                    {result.historicalMarketContext.fhfa.indexChangePercent >= 0 ? ", +" : ", "}
                    {result.historicalMarketContext.fhfa.indexChangePercent}% since prior sale). Sanity check only,
                    not an appraisal.
                  </p>
                  {result.historicalMarketContext.comparison.reliabilityBaseline && (
                    <Label
                      size="mini"
                      color={
                        { high: "green", moderate: "yellow", low: "grey" }[
                          result.historicalMarketContext.comparison.reliabilityBaseline
                        ] || "grey"
                      }
                    >
                      {result.historicalMarketContext.comparison.reliabilityBaseline} reliability
                    </Label>
                  )}
                </Segment>
              </Grid.Column>
            </Grid>
            {result?.fhfaCrossCheckNarrative && (
              <p className="text-grey" style={{ fontSize: "0.88em", marginTop: "8px" }}>
                {result.fhfaCrossCheckNarrative}
              </p>
            )}
          </>
        )}

      <CompetingListingsSection result={result} title="What Buyers Can Buy Instead" />

      <ConditionImpactSection
        result={result}
        conditionHasItems={conditionHasItems}
        showConditionDetail={showConditionDetail}
        setShowConditionDetail={setShowConditionDetail}
      />

      {/* Detailed AI analysis - collapsed by default on screen, always shown when printed */}
      {result?.detailedAnalysis && (
        <Segment secondary style={{ marginTop: "12px" }}>
          <div
            className="no-print"
            style={{ cursor: "pointer", display: "flex", justifyContent: "space-between" }}
            onClick={() => setShowDetailedAnalysis((v) => !v)}
          >
            <Header as="h5" style={{ marginBottom: 0 }}>Detailed AI Analysis</Header>
            <Icon name={showDetailedAnalysis ? "chevron up" : "chevron down"} />
          </div>
          <Header as="h5" className="print-only" style={{ marginBottom: 0 }}>Detailed AI Analysis</Header>
          <p
            className="strategy-collapsible"
            style={{ display: showDetailedAnalysis ? "block" : "none", marginTop: "10px", marginBottom: 0 }}
          >
            {result.detailedAnalysis}
          </p>
        </Segment>
      )}
    </>
  );
}

// Buyer Offer Pricing Strategy - an "offer decision tool," not a valuation
// report: what to offer, why, how far to realistically move, what the
// Seller/listing context suggests, what evidence supports it, and what to
// do next (see section 30's proposed order). Deliberately a SEPARATE
// component from the Seller path above rather than a shared branch, so the
// Seller (List Pricing Strategy) output can never be accidentally altered
// while reworking this one.
function BuyerOfferDecisionBody({
  result,
  compSearchSummary,
  canRegenerate,
  conditionHasItems,
  showBroaderMarketContext,
  setShowBroaderMarketContext,
  showConditionDetail,
  setShowConditionDetail,
  showMarketCrossChecks,
  setShowMarketCrossChecks,
}) {
  const inputs = result?.inputs || {};

  // Target Purchase Range: prefer the new Buyer-specific field: falls back
  // to expectedNegotiationLow/High only for generations saved before this
  // field existed, so old saved Strategies still show a range rather than
  // silently losing it.
  const targetLow = result?.targetPurchaseRangeLow || result?.expectedNegotiationLow;
  const targetHigh = result?.targetPurchaseRangeHigh || result?.expectedNegotiationHigh;

  // Asking-price difference - deterministic (never AI-computed), so the %
  // always matches the two price strings actually shown.
  const askingDiff = computePercentDifference(inputs.currentPrice, result?.recommendedListPrice);
  const askingDiffText = askingDiff
    ? `${Math.abs(askingDiff.percentDiff).toFixed(1)}% ${askingDiff.percentDiff <= 0 ? "below" : "above"} current asking price`
    : null;

  // Original list price + reduction - prefer the deterministic
  // originalListPriceContext (computed server-side from the same verified/
  // agent-entered source the prompt used); fall back to the raw form inputs
  // for generations saved before that field existed (no reduction % shown
  // in that fallback case, since we can't be sure it's consistent).
  const originalListPriceDisplay = result?.originalListPriceContext?.originalListPrice || formatDollar(inputs.originalListPrice);
  const reductionAmount = result?.originalListPriceContext?.reductionAmount;
  const reductionPercent = result?.originalListPriceContext?.reductionPercent;
  const hasReduction = Number.isFinite(reductionAmount) && reductionAmount !== 0;

  // Condition known/unknown - the exact placeholder string the prompt forces
  // when no condition info was given (see buildPricingStrategyPrompt) is the
  // one reliable signal that nothing was actually evaluated.
  const conditionUnknown = isConditionUnknown(result?.conditionImpact);

  const fhfaAvailable = result?.historicalMarketContext?.fhfa?.available &&
    Number.isFinite(result.historicalMarketContext.comparison?.broadMarketIndexedReference);
  const mortgageRate = result?.mortgageRateContext;
  const hasMarketCrossChecks = fhfaAvailable || Number.isFinite(mortgageRate?.currentRate);

  // Seller Purchase Price - prefer the deterministic sellerPurchaseContext;
  // fall back to raw form inputs for older generations.
  const sellerPurchasePriceDisplay = result?.sellerPurchaseContext?.price || formatDollar(inputs.sellerPurchasePrice);
  const sellerPurchaseDateDisplay = result?.sellerPurchaseContext?.date || inputs.sellerPurchaseDate;
  const homeStatusDisplay = inputs.homeStatus && inputs.homeStatus !== "unknown" ? HOME_STATUS_LABEL[inputs.homeStatus] : null;
  const hasSellerContext = !!(
    sellerPurchasePriceDisplay || sellerPurchaseDateDisplay || homeStatusDisplay ||
    inputs.timeOnMarket || originalListPriceDisplay || result?.sellerMotivationAnalysis
  );

  // "Difference from Prior Purchase Price" - reuses the SAME deterministic
  // impliedChangeSincePriorSale field the Seller pre-listing view already
  // computes (see generatePricingStrategy) - never labeled "profit"/
  // "equity," just the arithmetic fact, per rule 22/section 12.
  const priorPurchaseDiff = result?.impliedChangeSincePriorSale;

  // Top decision-summary metrics, in display order - built as an array so a
  // missing value (e.g. no verified original list price) just drops that
  // one column instead of leaving an awkward gap.
  const topMetrics = [
    {
      key: "offer",
      label: "Recommended Initial Offer",
      value: result?.recommendedListPrice,
      sub: askingDiffText,
      emphasize: true,
    },
    { key: "current", label: "Current List Price", value: inputs.currentPrice || null },
    originalListPriceDisplay
      ? {
          key: "original",
          label: "Original List Price",
          value: originalListPriceDisplay,
          sub: hasReduction
            ? `${reductionAmount < 0 ? "-" : "+"}$${Math.abs(reductionAmount).toLocaleString()} (${reductionPercent < 0 ? "-" : "+"}${Math.abs(reductionPercent).toFixed(1)}%)`
            : null,
        }
      : null,
    {
      key: "value",
      label: "Estimated Market Value",
      value: result?.estimatedValueLow && result?.estimatedValueHigh ? `${result.estimatedValueLow} – ${result.estimatedValueHigh}` : null,
    },
    targetLow && targetHigh
      ? { key: "target", label: "Target Purchase Range", value: `${targetLow} – ${targetHigh}` }
      : null,
    { key: "confidence", label: "Confidence", value: result?.confidence || "Unknown", isConfidence: true },
  ].filter(Boolean);

  return (
    <>
      {/* Top decision summary - what to offer, at a glance */}
      <Grid stackable columns={3} style={{ marginBottom: "4px" }}>
        {topMetrics.map((metric) => (
          <Grid.Column key={metric.key} style={{ marginBottom: "10px" }}>
            <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
              {metric.label.toUpperCase()}
            </div>
            {metric.isConfidence ? (
              <Label color={CONFIDENCE_COLOR[result?.confidence] || "grey"} style={{ marginTop: "2px" }}>
                {metric.value}
              </Label>
            ) : (
              <div style={{ fontSize: metric.emphasize ? "1.6em" : "1.15em", fontWeight: metric.emphasize ? 700 : 600 }}>
                {metric.value || "–"}
              </div>
            )}
            {metric.sub && (
              <div className="text-grey" style={{ fontSize: "0.85em", marginTop: "2px" }}>{metric.sub}</div>
            )}
          </Grid.Column>
        ))}
      </Grid>

      {result?.confidenceExplanation && (
        <p className="text-grey" style={{ fontSize: "0.88em", marginTop: 0 }}>
          {result.confidenceExplanation}
          {canRegenerate && (result?.confidence === "Low" || result?.confidence === "Low-Moderate") && (
            " Click “Edit Details & Regenerate” to fill in more information and re-run this Strategy for a better estimate."
          )}
        </p>
      )}

      {result?.strategySentence && (
        <p style={{ fontWeight: 500, marginBottom: "4px" }}>{result.strategySentence}</p>
      )}
      {result?.summary && <p className="text-grey" style={{ marginTop: 0 }}>{result.summary}</p>}

      {/* Why This Offer? - 3-5 high-value, property-specific cards (see rule 24) */}
      {result?.keyFactors?.length > 0 && (
        <>
          <Header as="h5" color="blue" style={{ marginBottom: "8px" }}>Why This Offer?</Header>
          <Grid stackable columns={2}>
            {result.keyFactors.map((factor, idx) => (
              <Grid.Column key={idx}>
                <Segment style={{ padding: "10px 14px" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                    <ImpactIcon impact={factor.impact} />
                    <strong style={{ fontSize: "0.85em", letterSpacing: "0.02em" }}>
                      {factor.title?.toUpperCase()}
                    </strong>
                  </div>
                  <p style={{ margin: "4px 0 0" }}>{factor.summary}</p>
                  {factor.supportingValue && (
                    <div className="text-grey" style={{ fontSize: "0.85em", marginTop: "4px" }}>
                      {factor.supportingValue}
                    </div>
                  )}
                </Segment>
              </Grid.Column>
            ))}
          </Grid>
        </>
      )}

      {/* Condition warning - surfaced early since it can materially change the recommendation,
          rather than buried near the bottom of the report (section 8). The full Condition Impact
          section still exists further down either way - this is just an early pointer to it. */}
      {conditionUnknown && (
        <Message warning size="small" style={{ marginTop: "12px" }}>
          <Message.Header>Condition not confirmed</Message.Header>
          <p style={{ marginTop: "4px", marginBottom: 0 }}>
            Adding condition, updates, or known issues may change the recommendation.
            {canRegenerate && " Click “Edit Details & Regenerate” to add this information."}
          </p>
        </Message>
      )}

      {/* Seller Context & Negotiation Signals - factual fields only; the narrative interpretation
          below is clearly separated and always phrased as a possibility, never a proven motivation
          (rules 21-22). */}
      {hasSellerContext && (
        <>
          <Header as="h5" color="blue" style={{ marginTop: "16px", marginBottom: "8px" }}>
            Seller Context &amp; Negotiation Signals
          </Header>
          <Segment style={{ padding: "12px 14px" }}>
            <Grid stackable columns={3}>
              {sellerPurchasePriceDisplay && (
                <Grid.Column>
                  <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
                    SELLER PURCHASE PRICE
                  </div>
                  <div style={{ fontSize: "1em", fontWeight: 600 }}>{sellerPurchasePriceDisplay}</div>
                </Grid.Column>
              )}
              {sellerPurchaseDateDisplay && (
                <Grid.Column>
                  <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
                    PURCHASE DATE
                  </div>
                  <div style={{ fontSize: "1em", fontWeight: 600 }}>{sellerPurchaseDateDisplay}</div>
                </Grid.Column>
              )}
              {homeStatusDisplay && (
                <Grid.Column>
                  <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
                    HOME STATUS
                  </div>
                  <div style={{ fontSize: "1em", fontWeight: 600 }}>{homeStatusDisplay}</div>
                </Grid.Column>
              )}
              {inputs.timeOnMarket && (
                <Grid.Column>
                  <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
                    DAYS ON MARKET
                  </div>
                  <div style={{ fontSize: "1em", fontWeight: 600 }}>{inputs.timeOnMarket}</div>
                </Grid.Column>
              )}
              {originalListPriceDisplay && (
                <Grid.Column>
                  <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
                    ORIGINAL LIST PRICE
                  </div>
                  <div style={{ fontSize: "1em", fontWeight: 600 }}>{originalListPriceDisplay}</div>
                </Grid.Column>
              )}
              {hasReduction && (
                <Grid.Column>
                  <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em" }}>
                    PRICE REDUCTION
                  </div>
                  <div style={{ fontSize: "1em", fontWeight: 600 }}>
                    {reductionAmount < 0 ? "-" : "+"}${Math.abs(reductionAmount).toLocaleString()}
                    {" / "}
                    {reductionPercent < 0 ? "-" : "+"}{Math.abs(reductionPercent).toFixed(1)}%
                  </div>
                </Grid.Column>
              )}
            </Grid>
            {priorPurchaseDiff && (
              <p style={{ marginTop: "10px", marginBottom: 0, fontSize: "0.88em" }}>
                <span className="text-grey">Difference from Prior Purchase Price: </span>
                <strong>
                  {priorPurchaseDiff.absoluteChange >= 0 ? "+" : ""}${priorPurchaseDiff.absoluteChange.toLocaleString()}
                  {" "}({priorPurchaseDiff.percentChange >= 0 ? "+" : ""}{priorPurchaseDiff.percentChange}%)
                </strong>
                <span className="text-grey"> vs. the current estimated market value - not the seller's profit or equity.</span>
              </p>
            )}
            {result?.sellerMotivationAnalysis && (
              <div style={{ marginTop: "10px" }}>
                <div className="text-grey" style={{ fontSize: "0.78em", fontWeight: 600, letterSpacing: "0.03em", marginBottom: "4px" }}>
                  POSSIBLE NEGOTIATION IMPLICATION
                </div>
                <p style={{ marginBottom: 0, fontSize: "0.92em" }}>{result.sellerMotivationAnalysis}</p>
              </div>
            )}
            <p className="text-grey" style={{ fontSize: "0.78em", marginTop: "10px", marginBottom: 0 }}>
              This information informs negotiation context, offer positioning, and timing considerations only -
              it does not establish the seller's actual motivation and does not factor into the recommended price
              or estimated market value above.
            </p>
          </Segment>
        </>
      )}

      {/* Offer approaches - the recommended one gets "— Recommended" appended, matching the
          Buyer-decision framing (section 13-16) */}
      {result?.pricingApproaches?.length > 0 && (
        <>
          <Header as="h5" color="blue" style={{ marginTop: "16px", marginBottom: "8px" }}>
            Offer Approaches
          </Header>
          <Grid stackable columns={3}>
            {result.pricingApproaches.map((approach, idx) => (
              <Grid.Column key={idx}>
                <Segment style={approach.recommended ? { borderTop: "3px solid #2185d0" } : undefined}>
                  {approach.recommended && (
                    <Label color="blue" ribbon style={{ marginBottom: "8px" }}>Recommended</Label>
                  )}
                  <Header as="h5" style={{ marginBottom: "2px" }}>
                    {approach.name}{approach.recommended ? " — Recommended" : ""}
                  </Header>
                  <div style={{ fontSize: "1.1em", fontWeight: 700, marginBottom: "6px" }}>
                    {approach.price}
                  </div>
                  <p style={{ fontSize: "0.92em" }}>{approach.explanation}</p>
                  {approach.advantages?.length > 0 && (
                    <List bulleted size="small">
                      {approach.advantages.map((item, i) => (
                        <List.Item key={i}>{item}</List.Item>
                      ))}
                    </List>
                  )}
                  {approach.tradeoffs?.length > 0 && (
                    <>
                      <div className="text-grey" style={{ fontWeight: 600, fontSize: "0.82em", marginTop: "8px" }}>
                        TRADEOFFS
                      </div>
                      <List bulleted size="small">
                        {approach.tradeoffs.map((item, i) => (
                          <List.Item key={i}>{item}</List.Item>
                        ))}
                      </List>
                    </>
                  )}
                </Segment>
              </Grid.Column>
            ))}
          </Grid>
        </>
      )}

      <ComparableSalesSection
        result={result}
        compSearchSummary={compSearchSummary}
        showBroaderMarketContext={showBroaderMarketContext}
        setShowBroaderMarketContext={setShowBroaderMarketContext}
      />

      <CompetingListingsSection result={result} title="Competing Listings" />

      <ValuationReconciliationSection result={result} />

      <ConditionImpactSection
        result={result}
        conditionHasItems={conditionHasItems}
        showConditionDetail={showConditionDetail}
        setShowConditionDetail={setShowConditionDetail}
      />

      {/* Market / Valuation Cross-Checks - FHFA + mortgage rate context, secondary to and visually
          smaller than comparable sales (never presented as a second valuation - rules 25/28).
          Collapsed by default; always expanded when printed via .strategy-collapsible. */}
      {hasMarketCrossChecks && (
        <Segment secondary style={{ marginTop: "16px" }}>
          <div
            style={{ cursor: "pointer", display: "flex", justifyContent: "space-between" }}
            onClick={() => setShowMarketCrossChecks((v) => !v)}
          >
            <div>
              <Header as="h5" style={{ marginBottom: "4px" }}>Market / Valuation Cross-Checks</Header>
              <p className="text-grey" style={{ margin: 0, fontSize: "0.85em" }}>
                Secondary sanity checks - comparable sales above remain the primary evidence for this
                recommendation.
              </p>
            </div>
            <Icon className="no-print" name={showMarketCrossChecks ? "chevron up" : "chevron down"} />
          </div>
          <div
            className="strategy-collapsible"
            style={{ display: showMarketCrossChecks ? "block" : "none", marginTop: "10px" }}
          >
            {fhfaAvailable && (
              <div style={{ marginBottom: Number.isFinite(mortgageRate?.currentRate) ? "14px" : 0 }}>
                <Label size="tiny" basic style={{ marginBottom: "6px" }}>FHFA Market Trend Reference</Label>
                <div style={{ fontWeight: 700, fontSize: "1.05em" }}>
                  ${Number(result.historicalMarketContext.comparison.broadMarketIndexedReference).toLocaleString()}
                </div>
                <p style={{ fontSize: "0.88em", marginTop: "6px", marginBottom: "4px" }}>
                  Based on the subject's prior sale ({result.historicalMarketContext.fhfa.priorPeriod?.period}) and
                  the {result.historicalMarketContext.fhfa.geography?.geoName} FHFA Expanded-Data HPI (
                  {result.historicalMarketContext.fhfa.currentPeriod?.period}
                  {result.historicalMarketContext.fhfa.indexChangePercent >= 0 ? ", +" : ", "}
                  {result.historicalMarketContext.fhfa.indexChangePercent}% since prior sale). Broad market-trend
                  sanity check only, not a second valuation - comparable sales above are the primary evidence.
                </p>
                {result?.fhfaCrossCheckNarrative && (
                  <p className="text-grey" style={{ fontSize: "0.85em", marginTop: "4px" }}>
                    {result.fhfaCrossCheckNarrative}
                  </p>
                )}
              </div>
            )}
            {Number.isFinite(mortgageRate?.currentRate) && (
              <div>
                <Label size="tiny" basic style={{ marginBottom: "6px" }}>30-Year Mortgage Rate</Label>
                <div style={{ fontWeight: 700, fontSize: "1.05em" }}>{mortgageRate.currentRate}%</div>
                <p style={{ fontSize: "0.88em", marginTop: "6px", marginBottom: 0 }}>
                  As of {mortgageRate.asOfDate} (source: Freddie Mac PMMS via FRED). Higher financing costs may
                  continue to constrain purchasing power and contribute to softer demand - broad market context
                  only, not a basis for adjusting the recommended offer.
                </p>
              </div>
            )}
          </div>
        </Segment>
      )}

      {/* Recommended Approach - the actionable conclusion (replaces Detailed AI Analysis for Buyer
          strategies, per section 28-29). Falls back to detailedAnalysis for generations saved before
          this field existed, so older Strategies don't lose their concluding narrative. */}
      {(result?.recommendedApproach || result?.detailedAnalysis) && (
        <Segment style={{ marginTop: "16px", borderTop: "3px solid #2185d0" }}>
          <Header as="h5" color="blue" style={{ marginBottom: "8px" }}>Recommended Approach</Header>
          <p style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}>
            {result.recommendedApproach || result.detailedAnalysis}
          </p>
        </Segment>
      )}
    </>
  );
}
